import {SharedColors} from "@fluentui/theme";
import {
    Dropdown,
    getFocusStyle,
    IButtonProps,
    IconButton,
    List,
    mergeStyleSets,
    ProgressIndicator,
    Stack,
    Text,
} from "@fluentui/react";
import React, {CSSProperties, FC, ReactNode, useCallback, useMemo,} from "react";
import {useTranslation} from "react-i18next";
import {EnumKeysHelper} from "../helpers/EnumKeysHelper";
import "../styles/DropdownFromEnum.css";
import {ShowApiError} from "./ShowApiError";
import {theme} from "../index";
import {useErrorMessage} from "../hooks/useErrorMessage";

interface IDropdownFromEnumProps {
    object: any;
    value: any;
    setValue: (v: any) => void;
    label?: string;
    showCancel?: boolean;
    inline?: boolean;
    id?: string;
    style?: CSSProperties;
    iconFromValue?: (t: any) => any;
    multiple?: boolean;
    errorMessage?: string;
    disabled?: boolean;
    placeholder?: string;
    rightSideIconButtonProps?: IButtonProps;
    loading?: boolean;
    className?: string;
    title?: string;
}

export const DropdownFromEnum: FC<IDropdownFromEnumProps> = ({
                                                                 style,
                                                                 className,
                                                                 loading,
                                                                 placeholder,
                                                                 rightSideIconButtonProps,
                                                                 disabled,
                                                                 multiple,
                                                                 errorMessage,
                                                                 iconFromValue,
                                                                 id,
                                                                 object,
                                                                 setValue,
                                                                 value,
                                                                 label,
                                                                 showCancel,
                                                                 inline,
                                                                 title,
                                                             }) => {
    const {t} = useTranslation();
    const {localErrorMessage, clearLocalErrorMessage} =
        useErrorMessage(errorMessage);

    const classNames = useMemo(
        () =>
            mergeStyleSets({
                itemCell: [
                    getFocusStyle(theme, {inset: -1}),
                    {
                        padding: 10,
                        boxSizing: "border-box",
                        borderBottom: `1px solid ${theme.semanticColors.bodyDivider}`,
                        display: "flex",
                        selectors: {
                            "&:hover": {
                                background: theme.palette?.neutralLighter,
                                cursor: "pointer",
                            },
                        },
                        minWidth: 200,
                    },
                ],
                selectedItemCell: [
                    getFocusStyle(theme, {inset: -1}),
                    {
                        background: theme.palette.neutralLight,
                        padding: "10px 10px 10px 8px",
                        boxSizing: "border-box",
                        borderBottom: `1px solid ${theme.semanticColors.bodyDivider}`,
                        borderLeft: `2px solid ${theme.palette.themePrimary}`,
                        display: "flex",
                        selectors: {
                            "&:hover": {cursor: "pointer"},
                        },
                        minWidth: 200,
                    },
                ],
            }),
        [theme],
    );

    const enumOfObject = useMemo(() => {
        const res = EnumKeysHelper(object);
        if (res.length === 0) {
            alert("EnumKeysHelper returned empty array! Check your enum!");
        }
        return res;
    }, [object]);

    const renderListCell = useCallback(
        (
            item?: { key: string; name: string; data: any } | undefined,
            index?: number,
            isScrolling?: boolean,
        ): ReactNode => {
            return (
                <Stack
                    horizontal
                    horizontalAlign={"space-between"}
                    style={{flex: 1, width: "100%"}}
                    className={
                        item?.key === enumOfObject[value]
                            ? classNames.selectedItemCell
                            : classNames.itemCell
                    }
                    onClick={disabled ? undefined : () => setValue(item?.key)}
                >
                    <Text key={item?.key}>{item?.name}</Text>
                    {iconFromValue ? iconFromValue(item?.data) : null}
                </Stack>
            );
        },
        [enumOfObject, classNames, value, setValue, iconFromValue, disabled],
    );

    const dropdown = useMemo<
        {
            key: string;
            text: string;
            data: any;
        }[]
    >(() => {
        const d = enumOfObject.map((m) => ({
            key: object[m],
            data: object[m],
            text: t(m),
        }));

        if (true || showCancel) {
            d.push({
                key: null,
                data: null,
                text: t("clear"),
            });
        }

        return d;
    }, [enumOfObject, showCancel, object, t]);

    if (inline) {
        return (
            <Stack title={title}>
                <List
                    title={title}
                    style={style}
                    id={id}
                    items={enumOfObject.map((m) => ({
                        key: m,
                        name: t(m),
                        data: m,
                    }))}
                    onRenderCell={renderListCell}
                />
                {localErrorMessage ? (
                    <ShowApiError errors={{"": [localErrorMessage]}} keys={[""]}/>
                ) : null}
                {loading && <ProgressIndicator/>}
            </Stack>
        );
    }

    return (
        <Stack
            title={title}
            id={id}
            horizontal
            style={{flex: 1, width: "100%", flexGrow: 1}}
            horizontalAlign={"stretch"}
            tokens={{childrenGap: 10}}
            className={className ?? "dropdown-from-enum-wrapper"}
            verticalAlign={"end"}
            styles={{
                root: {flex: 1, width: "100%", flexGrow: 1},
                inner: {flex: 1, width: "100%", flexGrow: 1},
            }}
        >
            <Dropdown
                title={title}
                key={dropdown.map((m) => m.key).join("-")}
                style={{flex: 1, width: "100%", flexGrow: 1}}
                styles={{
                    dropdown: {flex: 1, width: "100%", flexGrow: 1},
                    callout: {flex: 1, width: "100%", flexGrow: 1},
                    dropdownItem: {flex: 1, width: "100%", flexGrow: 1},
                    root: {flex: 1, width: "100%", flexGrow: 1},
                    dropdownItems: {flex: 1, width: "100%", flexGrow: 1},
                }}
                disabled={disabled}
                errorMessage={localErrorMessage}
                placeHolder={placeholder}
                dropdownWidth={300}
                onFocus={(e) => {
                    clearLocalErrorMessage();
                }}
                onClick={(e) => {
                    clearLocalErrorMessage();
                }}
                selectedKeys={multiple ? value : undefined}
                multiSelect={multiple}
                onRenderOption={(props, defaultRender) => {
                    const additionalProps = {
                        color: props?.data === null ? SharedColors.red10 : undefined,
                        fontWeight: props?.data === null ? "bold" : undefined,
                    };

                    return (
                        <Stack
                            className={"dropdown-enum-item"}
                            horizontal
                            styles={{
                                root: {flex: 1, width: "100%", flexGrow: 1},
                                inner: {flex: 1, width: "100%", flexGrow: 1},
                            }}
                            color={props?.data === null ? SharedColors.red10 : undefined}
                            style={{
                                flex: 1,
                                width: multiple ? "90%" : "100%",
                                flexGrow: 1,
                                marginLeft: multiple ? 25 : undefined,
                            }}
                            horizontalAlign={"space-between"}
                            onClick={
                                disabled
                                    ? undefined
                                    : () => (multiple ? () => {
                                    } : setValue(props?.key))
                            }
                        >
                            <Text key={props?.key} style={additionalProps}>
                                {props?.text}
                            </Text>
                            {iconFromValue ? iconFromValue(props?.data) : null}
                        </Stack>
                    );
                }}
                options={dropdown}
                placeholder={placeholder ? t(placeholder) : undefined}
                selectedKey={multiple ? undefined : value}
                onChange={(_, v) => (v ? setValue(v.data) : null)}
                label={label ? t(label) : undefined}
            />
            {showCancel ? (
                <IconButton
                    style={{color: SharedColors.red10}}
                    iconProps={{iconName: "Cancel"}}
                    onClick={disabled ? undefined : () => setValue(null)}
                />
            ) : null}
            {rightSideIconButtonProps ? (
                <IconButton disabled={disabled} {...rightSideIconButtonProps} />
            ) : null}
        </Stack>
    );
};
